import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  Button,
  ErrorNote,
  InlineNote,
  Notice,
  Spinner,
  Tag,
  buttonClasses,
} from "../components/primitives";
import { api } from "../lib/api";
import { money } from "../lib/format";
import type { ProposalOption } from "../lib/types";

/**
 * Where an emailed proposal's links open: the three reports, priced, and one
 * button.
 *
 * **Opening it spends nothing.** A mail scanner fetches every link in a
 * message, so the link only *selects* (`?option=2`), and the order is the
 * button's POST — the same rule the sign-in link follows. The server decides
 * everything that matters: whose proposal it is (anybody else's is a 404),
 * whether it is still open, and whether the credit is there.
 *
 * Not in the navigation. It is reached from a mail, and a page in the nav
 * would need something to list — the reply is how most people order.
 */
export function Order() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const proposal = useQuery({
    queryKey: ["proposal", id],
    queryFn: () => api.proposal(id),
    // A proposal is written a few seconds after the mail arrives; somebody
    // opening the link from a forwarded copy may get here first.
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 3_000 : false),
    retry: false,
  });
  const preselected = Number(params.get("option")) || null;
  const [picked, setPicked] = useState<number | null>(preselected);

  const order = useMutation({
    mutationFn: (option: number) => api.orderProposal(id, option),
    onSuccess: ({ run_id }) => navigate(`/engagements/${encodeURIComponent(run_id)}`),
  });

  if (proposal.isLoading) return <Spinner />;
  if (proposal.error) return <ErrorNote error={proposal.error} />;
  const data = proposal.data!;
  const chosen = data.options.find((o) => o.number === (picked ?? 0)) ?? null;

  return (
    <div className="max-w-read space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Your proposal</h1>
        <blockquote className="mt-2 border-l-2 border-line-strong pl-3 text-sm text-ink-2">
          {data.request.split("\n").slice(0, 6).join(" ")}
        </blockquote>
      </div>

      {data.status === "pending" && <Spinner label="Preparing the proposal" />}

      {data.status === "failed" && (
        <Notice tone="warning" title="This request could not be answered">
          <p>Something went wrong while preparing the proposal. Send the request again in a few minutes.</p>
        </Notice>
      )}

      {data.status === "unmatched" && (
        <Notice tone="warning" title="None of our playbooks fits this request">
          <p>
            <Link
              to={`/new/prompt-me?from=${encodeURIComponent(data.id)}`}
              className="underline"
            >
              Run it with Prompt Me
            </Link>
            , in your own words — your question is already filled in.
          </p>
        </Notice>
      )}

      {data.status === "ordered" && (
        <Notice tone="neutral" title="Ordered">
          <p>
            {data.options.find((o) => o.number === data.chosen)?.title ?? "This report"} has
            been ordered.{" "}
            {data.run_id && (
              <Link to={`/engagements/${encodeURIComponent(data.run_id)}`} className="underline">
                Follow it here
              </Link>
            )}
          </p>
        </Notice>
      )}

      {data.status === "expired" && (
        <Notice tone="neutral" title="This proposal has expired">
          <p>
            Reply to the email to get a fresh one, or{" "}
            <Link to="/" className="underline">
              start a report here
            </Link>
            .
          </p>
        </Notice>
      )}

      {data.options.length > 0 && (
        <fieldset className="space-y-2" disabled={data.status !== "open" || order.isPending}>
          <legend className="sr-only">Choose a report</legend>
          {data.options.map((option) => (
            <OptionRow
              key={option.number}
              option={option}
              selected={picked === option.number || data.chosen === option.number}
              onSelect={() => setPicked(option.number)}
            />
          ))}
        </fieldset>
      )}

      {data.status === "open" && (
        <div className="space-y-3">
          <p className="text-xs leading-relaxed text-muted">
            The amount shown is held from your credit when the report starts, and you
            pay what it actually costs. Available: {money(data.balance_usd)}.
          </p>
          {chosen?.hold_usd != null && chosen.hold_usd > data.balance_usd && (
            <InlineNote tone="warning">
              This needs more credit than is available.{" "}
              <Link to="/billing" className="underline">
                Add credit
              </Link>
            </InlineNote>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={!chosen || order.isPending}
              onClick={() => chosen && order.mutate(chosen.number)}
            >
              {order.isPending ? "Starting…" : chosen ? `Start: ${chosen.title}` : "Choose a report"}
            </Button>
            <Link to="/engagements" className={buttonClasses("ghost", "md")}>
              Not now
            </Link>
          </div>
          {order.error && <ErrorNote error={order.error} />}
        </div>
      )}
    </div>
  );
}

function OptionRow({
  option,
  selected,
  onSelect,
}: {
  option: ProposalOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`card flex cursor-pointer gap-3 border-l-2 px-4 py-3 ${
        selected ? "border-l-accent bg-surface-2" : "border-l-transparent hover:bg-surface-2"
      }`}
    >
      <input
        type="radio"
        name="option"
        className="mt-1 shrink-0 accent-[var(--accent)]"
        checked={selected}
        onChange={onSelect}
      />
      <span className="min-w-0 flex-1 space-y-1">
        <span className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="font-medium text-ink">
            {option.number}. {option.title}
          </span>
          <span className="font-mono text-sm tabular-nums text-ink">
            {option.hold_usd == null ? "—" : `up to ${money(option.hold_usd)}`}
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-2 text-xs text-ink-2">
          {option.report}
          <Tag>{option.depth}</Tag>
        </span>
        {option.scope && (
          <span className="block text-xs text-ink-2">
            <span className="text-muted">For:</span> {option.scope}
          </span>
        )}
        {option.why && <span className="block text-sm text-ink-2">{option.why}</span>}
        {option.questions && option.questions.length > 0 && (
          <details className="text-xs text-ink-2">
            <summary className="cursor-pointer text-muted">
              Answers your {option.questions.length}{" "}
              {option.questions.length === 1 ? "question" : "questions"}
            </summary>
            <ol className="mt-1 list-decimal space-y-0.5 pl-5">
              {option.questions.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ol>
          </details>
        )}
      </span>
    </label>
  );
}
